type QueuedCall = () => void;

export interface JarabotUserIdentity {
	name?: string;
	// eslint-disable-next-line camelcase
	country_code?: string;
}

export interface JarabotContactAttributes {
	'contract.type'?: string;
	'contract.activeProducts'?: string[];
	'contract.availableAmountState'?: string;
	'contract.customerBlockingReasons'?: string[];
	'customer.kyc.isOutdated'?: boolean;
	'customer.amlLegalReview.isRequested'?: boolean;
}

export const JARABOT_EVENTS = {
	CUSTOMER_STUCK_ON_SMS: 'customer_stuck_on_sms_in_gate',
	CUSTOMER_STUCK_ON_SMS_HANDOFF: 'customer_stuck_on_sms_in_gate_handoff',
	CUSTOMER_STUCK_ON_IDENTITY_VERIFICATION: 'customer_stuck_on_identity_verification',
	CUSTOMER_INTERRUPTED_IDENTITY_VERIFICATION: 'customer_interrupted_identity_verification',
	CUSTOMER_STUCK_ON_CREDIT_APPLICATION_START: 'customer_stuck_on_credit_application_start',
	CUSTOMER_KYC_NAME_MISMATCH_CANCELLATION: 'customer_KYC_name_mismatch_cancellation_in_gate',
} as const;

export type JarabotEvent = (typeof JARABOT_EVENTS)[keyof typeof JARABOT_EVENTS];

const pendingQueue: QueuedCall[] = [];
let isReady = false;

const isDev = process.env.TARGET_ENV !== 'prod';

function log(method: string, payload?: unknown): void {
	if (isDev) {
		// eslint-disable-next-line no-console
		console.log('[Jarabot]', method, payload ?? '');
	}
}

if (typeof window !== 'undefined') {
	if (window.$jarabot?.hasLoaded) {
		isReady = true;
	} else {
		window.addEventListener(
			'jarabot:ready',
			() => {
				isReady = true;
				pendingQueue.splice(0).forEach((fn) => fn());
			},
			{ once: true }
		);
	}

	if (isDev) {
		(['jarabot:on-message', 'jarabot:open', 'jarabot:close'] as const).forEach((eventName) => {
			window.addEventListener(eventName, (e: Event) => {
				log(`incoming:${eventName}`, (e as CustomEvent).detail);
			});
		});
	}
}

function schedule(fn: QueuedCall): void {
	if (isReady) {
		fn();
	} else {
		pendingQueue.push(fn);
	}
}

export const jarabotClient = {
	sendMessage(event: JarabotEvent, detailInfo?: Record<string, unknown>, hidden?: boolean): void {
		log('sendMessage', { event, detailInfo, hidden });
		schedule(() => {
			window.$jarabot?.sendMessage({ message: event, detail_info: detailInfo, hidden });
		});
	},

	setUser(identifier: string, attrs: JarabotUserIdentity): void {
		log('setUser', { identifier, attrs });
		schedule(() => {
			window.$jarabot?.setUser(identifier, attrs as Record<string, unknown>);
		});
	},

	setCustomAttributes(attrs: JarabotContactAttributes): void {
		log('setCustomAttributes', attrs);
		schedule(() => {
			window.$jarabot?.setCustomAttributes(attrs as Record<string, unknown>);
		});
	},

	setConversationCustomAttributes(attrs: Record<string, unknown>): void {
		log('setConversationCustomAttributes', attrs);
		schedule(() => {
			window.$jarabot?.setConversationCustomAttributes(attrs);
		});
	},

	deleteCustomAttribute(key: string): void {
		log('deleteCustomAttribute', key);
		schedule(() => {
			window.$jarabot?.deleteCustomAttribute(key);
		});
	},

	deleteConversationCustomAttribute(key: string): void {
		log('deleteConversationCustomAttribute', key);
		schedule(() => {
			window.$jarabot?.deleteConversationCustomAttribute(key);
		});
	},

	reset(): void {
		log('reset');
		schedule(() => {
			window.$jarabot?.reset();
		});
	},

	toggle(state?: 'open' | 'close'): void {
		log('toggle', state);
		schedule(() => {
			window.$jarabot?.toggle(state);
		});
	},
};
